import { useEffect, useRef, useState } from "react";
import { IoPencil, IoTrash, IoAdd, IoCheckmark, IoClose } from "react-icons/io5";
import { useNotificationContext } from "context/NotificationContext";
import { addClientSchedule, getClient, editClientSchedule, deleteClientSchedule } from "../clientApi";

const scheduleStatuses = ["scheduled", "completed", "cancelled"];

const getStatusBadge = (status) => {
  switch (status) {
    case "completed":
      return "bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-200";
    case "cancelled":
      return "bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-200";
    default:
      return "bg-brand-100 text-brand-700 dark:bg-brand-900 dark:text-brand-200";
  }
};

const ClientSchedule = ({ client, showToast, setClient }) => {
  const { refreshNotifications } = useNotificationContext();
  const [editedSchedules, setEditedSchedules] = useState([]);
  const [newSchedule, setNewSchedule] = useState({
    scheduledTime: "",
    note: "",
    scheduleStatus: "scheduled",
  });
  
  const [scheduleErrors, setScheduleErrors] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [showAddStatusDropdown, setShowAddStatusDropdown] = useState(false);
  const [showEditStatusDropdown, setShowEditStatusDropdown] = useState(null);
  const addStatusDropdownRef = useRef(null);
  const editStatusDropdownRef = useRef(null);

  useEffect(() => {
    const schedules = Array.isArray(client?.schedules) ? client.schedules : [];
    setEditedSchedules(schedules);
  }, [client]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (addStatusDropdownRef.current && !addStatusDropdownRef.current.contains(event.target)) {
        setShowAddStatusDropdown(false);
      }
      if (editStatusDropdownRef.current && !editStatusDropdownRef.current.contains(event.target)) {
        setShowEditStatusDropdown(null);
      }
    };

    if (showAddStatusDropdown || showEditStatusDropdown !== null) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
    return undefined;
  }, [showAddStatusDropdown, showEditStatusDropdown]);

  const handleAddSchedule = async () => {
    const errors = {};
    if (!newSchedule.scheduledTime) errors.scheduledTime = "Scheduled time is required";
    if (!newSchedule.note || !newSchedule.note.trim()) errors.note = "Note is required";

    if (Object.keys(errors).length > 0) {
      setScheduleErrors(errors);
      return;
    }

    setScheduleErrors({});
    try {
      await addClientSchedule(client._id || client.id, {
        scheduledTime: newSchedule.scheduledTime,
        note: newSchedule.note,
        scheduleStatus: newSchedule.scheduleStatus,
      });

      showToast("Schedule added successfully", "success");
      
      // Refresh notification count immediately after successful add
      await refreshNotifications();
      
      // Refresh client data to get the new schedule from API
      const updatedClientData = await getClient(client._id || client.id);
      const clientDataToUse = updatedClientData?.data || updatedClientData;
      
      setClient(clientDataToUse);
      setEditedSchedules(clientDataToUse.schedules || []);

      setNewSchedule({ scheduledTime: "", note: "", scheduleStatus: "scheduled" });
      setIsModalOpen(false);
    } catch (error) {
      console.error("Error adding schedule:", error);
      const apiErrors = error?.response?.data?.errors || error?.response?.data || null;
      const fieldErrors = {};

      if (apiErrors && typeof apiErrors === "object") {
        if (Array.isArray(apiErrors)) {
          apiErrors.forEach((err) => {
            if (err.path && err.msg) {
              fieldErrors[err.path] = err.msg;
            }
          });
        } else {
          if (apiErrors.scheduledTime) fieldErrors.scheduledTime = apiErrors.scheduledTime;
          if (apiErrors.note) fieldErrors.note = apiErrors.note;
          if (apiErrors.message && Object.keys(fieldErrors).length === 0) {
            fieldErrors.submit = apiErrors.message;
          }
        }
      }

      if (Object.keys(fieldErrors).length > 0) {
        setScheduleErrors(fieldErrors);
      } else {
        showToast(error?.message || "Failed to add schedule", "error");
      }
      } finally {
      }
  };

  const handleDeleteSchedule = async (index) => {
    const schedule = editedSchedules[index];
    const scheduleId = schedule._id || schedule.id;
    if (!scheduleId) {
      // fallback to local removal
      setEditedSchedules((prev) => prev.filter((_, i) => i !== index));
      return;
    }

    try {
      await deleteClientSchedule(client._id || client.id, scheduleId);
      showToast("Schedule deleted successfully", "success");

      const updatedClientData = await getClient(client._id || client.id);
      const clientDataToUse = updatedClientData?.data || updatedClientData;
      setClient(clientDataToUse);
      setEditedSchedules(clientDataToUse.schedules || []);

      // Update notification counts when schedule list changes
      await refreshNotifications();
    } catch (error) {
      console.error("Error deleting schedule:", error);
      showToast(error?.message || "Failed to delete schedule", "error");
    } finally {
    }
  };

  const handleEditSchedule = (index) => {
    setEditingIndex(index);
  };

  const handleSaveEdit = async (index) => {
    const schedule = editedSchedules[index];
    if (!schedule?.scheduledTime || !schedule?.note) {
      showToast("Please fill in all fields", "error");
      return;
    }

    const scheduleId = schedule._id || schedule.id;
    if (!scheduleId) {
      showToast("Schedule id missing", "error");
      return;
    }

    try {
      await editClientSchedule(client._id || client.id, scheduleId, {
        scheduledTime: schedule.scheduledTime,
        note: schedule.note,
        scheduleStatus: schedule.scheduleStatus,
      });

      showToast("Schedule updated successfully", "success");

      const updatedClientData = await getClient(client._id || client.id);
      const clientDataToUse = updatedClientData?.data || updatedClientData;
      setClient(clientDataToUse);
      setEditedSchedules(clientDataToUse.schedules || []);
      setEditingIndex(null);
      await refreshNotifications();
    } catch (error) {
      console.error("Error updating schedule:", error);
      showToast(error?.message || "Failed to update schedule", "error");
    } finally {
    }
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
  };

  const handleScheduleChange = (index, field, value) => {
    setEditedSchedules((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  // bulk save/cancel removed — edits are per-item via `editClientSchedule`

  const formatDate = (value) => {
    if (!value) return "";
    const date = new Date(value);
    return date.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm dark:border-navy-700 dark:bg-navy-900">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-brand-600 dark:text-brand-300">
            Client Schedule
          </p>
          <h3 className="text-lg font-semibold text-navy-900 dark:text-white">Appointment planner</h3>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => setIsModalOpen(true)}
            className="ml-auto inline-flex items-center gap-2 rounded-full bg-white px-3 py-2 text-xs font-semibold text-brand-600 ring-1 ring-brand-300 transition hover:bg-brand-50 dark:bg-navy-800 dark:text-brand-300 dark:ring-brand-500/20 sm:px-4 sm:py-2 sm:text-sm"
          >
            <IoAdd className="text-sm" />
            Add Schedule
          </button>
        </div>
      </div>

      <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-3">
        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3 dark:border-navy-700 dark:bg-navy-800">
          <p className="text-xs text-brand-600 dark:text-brand-300">Total</p>
          <p className="mt-1 text-2xl font-semibold text-navy-900 dark:text-white">{editedSchedules.length}</p>
          <p className="text-xs text-slate-500 dark:text-slate-400">schedules</p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3 dark:border-navy-700 dark:bg-navy-800">
          <p className="text-xs text-brand-600 dark:text-brand-300">Latest</p>
          <p className="mt-1 text-xl font-semibold capitalize text-navy-900 dark:text-white">
            {editedSchedules[0]?.scheduleStatus || "—"}
          </p>
          <p className="text-xs text-slate-500 dark:text-slate-400">status</p>
        </div>
        <div className="col-span-2 rounded-2xl border border-slate-200 bg-slate-50 p-3 dark:border-navy-700 dark:bg-navy-800 md:col-span-1">
          <p className="text-xs text-brand-600 dark:text-brand-300">Edit mode</p>
          <p className="mt-1 text-lg font-semibold text-navy-900 dark:text-white">Per-item</p>
          <p className="text-xs text-slate-500 dark:text-slate-400">single schedule edit at a time</p>
        </div>
      </div>

      <div className="rounded-[20px] border border-slate-200 bg-slate-50 p-3 dark:border-navy-700 dark:bg-navy-800">
        {editedSchedules.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-200 bg-white p-4 text-center text-xs text-slate-600 dark:border-navy-700 dark:bg-navy-900 dark:text-slate-300">
            No schedules yet. Enable edit to add one.
          </div>
        ) : (
          <div className="max-h-[380px] overflow-y-auto">
            <div className="space-y-2">
              {editedSchedules.map((schedule, index) => (
                <div
                  key={schedule._id || schedule.id || index}
                  className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm transition hover:border-brand-300 hover:shadow-md dark:border-navy-700 dark:bg-navy-900"
                >
                  {editingIndex === index ? (
                    <div className="space-y-2">
                      <div className="grid gap-2">
                        <label className="text-xs font-semibold text-slate-700 dark:text-slate-200">Time</label>
                        <input
                          type="datetime-local"
                          value={schedule.scheduledTime?.split(".")[0] || ""}
                          onChange={(e) => handleScheduleChange(index, "scheduledTime", e.target.value)}
                          className="w-full rounded-lg border border-slate-200 bg-white px-2 py-2 text-xs text-slate-700 outline-none transition focus:border-brand-400 dark:border-navy-600 dark:bg-navy-900 dark:text-white"
                        />
                      </div>
                      <div className="grid gap-2">
                        <label className="text-xs font-semibold text-slate-700 dark:text-slate-200">Note</label>
                        <textarea
                          rows="2"
                          value={schedule.note || ""}
                          onChange={(e) => handleScheduleChange(index, "note", e.target.value)}
                          className="w-full rounded-lg border border-slate-200 bg-white px-2 py-2 text-xs text-slate-700 outline-none transition focus:border-brand-400 dark:border-navy-600 dark:bg-navy-900 dark:text-white"
                        />
                      </div>
                      <div ref={editStatusDropdownRef} className="relative">
                        <label className="text-xs font-semibold text-slate-700 dark:text-slate-200">Status</label>
                        <button
                          onClick={() =>
                            setShowEditStatusDropdown(showEditStatusDropdown === index ? null : index)
                          }
                          className="w-full rounded-lg border border-slate-200 bg-white px-2 py-2 text-left text-xs text-slate-700 transition hover:border-brand-400 focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-900 dark:text-white"
                        >
                          {schedule.scheduleStatus}
                        </button>
                        {showEditStatusDropdown === index && (
                          <div className="absolute top-full left-0 right-0 z-50 mt-1 max-h-32 overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-lg dark:border-navy-600 dark:bg-navy-900">
                            {scheduleStatuses.map((status) => (
                              <button
                                key={status}
                                type="button"
                                onClick={() => {
                                  handleScheduleChange(index, "scheduleStatus", status);
                                  setShowEditStatusDropdown(null);
                                }}
                                className="w-full px-3 py-2 text-left text-xs text-slate-700 transition hover:bg-brand-50 dark:text-slate-200 dark:hover:bg-brand-500/10"
                              >
                                {status}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={() => handleSaveEdit(index)}
                          className="inline-flex items-center gap-1 rounded-full bg-brand-600 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-brand-700"
                        >
                          <IoCheckmark className="text-sm" />
                          Save
                        </button>
                        <button
                          onClick={handleCancelEdit}
                          className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:bg-slate-200 dark:bg-navy-800 dark:text-slate-200"
                        >
                          <IoClose className="text-sm" />
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="mb-1 flex items-center gap-2">
                          <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
                            {schedule.scheduleStatus || "scheduled"}
                          </p>
                          <span className={`rounded-full px-2 py-0.5 text-xs font-semibold flex-shrink-0 ${getStatusBadge(schedule.scheduleStatus)}`}>
                            {schedule.scheduleStatus || "scheduled"}
                          </span>
                        </div>
                        <p className="text-sm font-semibold text-navy-900 dark:text-white truncate">
                          {formatDate(schedule.scheduledTime)}
                        </p>
                        <p className="mt-1 text-xs text-slate-600 line-clamp-2 dark:text-slate-400">
                          {schedule.note}
                        </p>
                        <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">
                          Created: {formatDate(schedule.createdAt)}
                        </p>
                      </div>

                      {editingIndex !== index ? (
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <button
                            onClick={() => handleEditSchedule(index)}
                            className="rounded-full bg-slate-100 p-1.5 text-slate-600 transition hover:bg-slate-200 dark:bg-navy-800 dark:text-slate-200"
                            title="Edit"
                          >
                            <IoPencil className="text-xs" />
                          </button>
                          <button
                            onClick={() => handleDeleteSchedule(index)}
                            className="rounded-full bg-slate-100 p-1.5 text-red-600 transition hover:bg-red-100 dark:bg-navy-800 dark:text-red-400"
                            title="Delete"
                          >
                            <IoTrash className="text-xs" />
                          </button>
                        </div>
                      ) : null}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-3 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-[28px] bg-white p-5 shadow-2xl dark:bg-navy-900">
            <div className="mb-4 flex items-center justify-between">
              <div>
                <h4 className="text-lg font-semibold text-navy-900 dark:text-white">Add schedule</h4>
                <p className="text-xs text-slate-500 dark:text-slate-400">Add a new schedule entry</p>
              </div>
              <button
                onClick={() => setIsModalOpen(false)}
                className="rounded-full bg-slate-100 p-2 text-slate-600 transition hover:bg-slate-200 dark:bg-navy-800 dark:text-slate-200"
              >
                <IoClose className="text-base" />
              </button>
            </div>

            <div className="space-y-3">
              <div className="grid gap-2">
                <label className="text-xs font-semibold text-slate-700 dark:text-slate-200">Scheduled time</label>
                <input
                  type="datetime-local"
                  value={newSchedule.scheduledTime}
                  onChange={(e) => {
                    setNewSchedule((prev) => ({ ...prev, scheduledTime: e.target.value }));
                    setScheduleErrors((prev) => ({ ...prev, scheduledTime: null }));
                  }}
                  className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 outline-none transition focus:border-brand-400 dark:border-navy-600 dark:bg-navy-900 dark:text-white"
                />
                {scheduleErrors.scheduledTime && (
                  <p className="text-xs text-red-500">{scheduleErrors.scheduledTime}</p>
                )}
              </div>

              <div className="grid gap-2">
                <label className="text-xs font-semibold text-slate-700 dark:text-slate-200">Note</label>
                <textarea
                  rows="3"
                  value={newSchedule.note}
                  onChange={(e) => {
                    setNewSchedule((prev) => ({ ...prev, note: e.target.value }));
                    setScheduleErrors((prev) => ({ ...prev, note: null }));
                  }}
                  className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 outline-none transition focus:border-brand-400 dark:border-navy-600 dark:bg-navy-900 dark:text-white"
                />
                {scheduleErrors.note && (
                  <p className="text-xs text-red-500">{scheduleErrors.note}</p>
                )}
              </div>

              <div ref={addStatusDropdownRef} className="relative">
                <label className="text-xs font-semibold text-slate-700 dark:text-slate-200">Status</label>
                <button
                  onClick={() => setShowAddStatusDropdown((prev) => !prev)}
                  className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-left text-sm text-slate-700 transition hover:border-brand-400 dark:border-navy-600 dark:bg-navy-900 dark:text-white"
                >
                  {newSchedule.scheduleStatus}
                </button>
                {showAddStatusDropdown && (
                  <div className="absolute left-0 right-0 z-50 mt-1 max-h-32 overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-lg dark:border-navy-600 dark:bg-navy-900">
                    {scheduleStatuses.map((status) => (
                      <button
                        key={status}
                        type="button"
                        onClick={() => {
                          setNewSchedule((prev) => ({ ...prev, scheduleStatus: status }));
                          setShowAddStatusDropdown(false);
                        }}
                        className="w-full px-3 py-2 text-left text-sm text-slate-700 transition hover:bg-brand-50 dark:text-slate-200 dark:hover:bg-brand-500/10"
                      >
                        {status}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="mt-5 flex items-center gap-2 flex-wrap">
              <button
                onClick={handleAddSchedule}
                className="flex-1 min-w-[120px] rounded-full bg-brand-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-brand-700"
              >
                Add
              </button>
              <button
                onClick={() => {
                  setIsModalOpen(false);
                  setNewSchedule({ scheduledTime: "", note: "", scheduleStatus: "scheduled" });
                }}
                className="flex-1 min-w-[120px] rounded-full border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-100 dark:border-navy-600 dark:bg-navy-800 dark:text-slate-200"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientSchedule;
